import {PlaneConstraintType, ReferenceCommandModeSource, ReferenceEntitySemanticType,} from '../ReferenceFeatureTypes';

/**
 * 基准面模式推断输入项。
 */
export interface PlaneModeInferenceInput {
  /** 槽位键值。 */
  slot: string;
  /** 参考类别。 */
  kind: string;
}

/**
 * 基准面模式迁移结果。
 */
export interface PlaneModeTransitionResult {
  /** 推断后约束类型。 */
  constraint: PlaneConstraintType;
  /** 是否由自动推断得到。 */
  inferred: boolean;
}

/**
 * 基准面命令规则引擎。
 * 职责：
 * 1. 管理选择组合到可用约束的映射；
 * 2. 管理自动约束推断优先级；
 * 3. 管理删除参考后的约束迁移。
 */
export class PlaneCommandRuleEngine {
  /**
   * 判断是否为平面类参考。
   *
   * @param kind 参考语义类型。
   * @returns 是否为平面类。
   */
  private static isPlaneLike(kind: string | undefined): boolean {
    return kind === ReferenceEntitySemanticType.DatumPlane || kind === ReferenceEntitySemanticType.FeatureFace;
  }

  /**
   * 判断是否为曲面类参考。
   *
   * @param kind 参考语义类型。
   * @returns 是否为曲面类。
   */
  private static isSurfaceLike(kind: string | undefined): boolean {
    return kind === ReferenceEntitySemanticType.FeatureSurface;
  }

  /**
   * 判断是否为线类参考。
   *
   * @param kind 参考语义类型。
   * @returns 是否为线类。
   */
  private static isLinear(kind: string | undefined): boolean {
    return kind === ReferenceEntitySemanticType.FeatureLine
      || kind === ReferenceEntitySemanticType.FeatureCurve
      || kind === ReferenceEntitySemanticType.DatumAxis;
  }

  /**
   * 判断是否为点类参考。
   *
   * @param kind 参考语义类型。
   * @returns 是否为点类。
   */
  private static isPointLike(kind: string | undefined): boolean {
    return kind === ReferenceEntitySemanticType.FeaturePoint || kind === ReferenceEntitySemanticType.DatumPoint;
  }

  /**
   * 根据当前选择类型返回可用约束集合。
   * @param kinds 当前已选参考类型。
   */
  public static resolveAvailableConstraints(kinds: ReferenceEntitySemanticType[]): PlaneConstraintType[] {
    const first = kinds[0];
    const second = kinds[1];
    const third = kinds[2];


      /**
     * 曲面 + 平面混合场景允许在“垂直/平行”之间切换：
     * 1. 第二参考补入平面后默认仍按垂直求解；
     * 2. PropertyManager 需要允许用户把平面槽位改成平行；
     * 3. 因此底层可用约束必须同时放开 `Perpendicular` 与 `Parallel`。
     */
    const isSurfacePlanePair = (
      (PlaneCommandRuleEngine.isSurfaceLike(first) && PlaneCommandRuleEngine.isPlaneLike(second))
      || (PlaneCommandRuleEngine.isPlaneLike(first) && PlaneCommandRuleEngine.isSurfaceLike(second))
    );


      if (!first) {
      return [PlaneConstraintType.Auto];
    }
    if (PlaneCommandRuleEngine.isPlaneLike(first) && !second) {
      return [PlaneConstraintType.Distance, PlaneConstraintType.Coincident];
    }
    if (PlaneCommandRuleEngine.isSurfaceLike(first) && !second) {
      return [PlaneConstraintType.Tangent];
    }
    if (PlaneCommandRuleEngine.isPlaneLike(first) && PlaneCommandRuleEngine.isPlaneLike(second)) {
      return [PlaneConstraintType.MidPlane, PlaneConstraintType.Distance, PlaneConstraintType.Parallel, PlaneConstraintType.Coincident];
    }
    if (isSurfacePlanePair) {
      return [PlaneConstraintType.Perpendicular, PlaneConstraintType.Parallel];
    }
    if (PlaneCommandRuleEngine.isPlaneLike(first) && PlaneCommandRuleEngine.isPointLike(second)) {
      return [PlaneConstraintType.Parallel, PlaneConstraintType.Perpendicular, PlaneConstraintType.Coincident];
    }
    if (PlaneCommandRuleEngine.isPlaneLike(first) && PlaneCommandRuleEngine.isLinear(second)) {
      return [PlaneConstraintType.Perpendicular, PlaneConstraintType.Coincident, PlaneConstraintType.Angle];
    }
    if (
      PlaneCommandRuleEngine.isPointLike(first)
      && (PlaneCommandRuleEngine.isPlaneLike(second) || PlaneCommandRuleEngine.isSurfaceLike(second))
      && PlaneCommandRuleEngine.isPlaneLike(third)
    ) {
      return [PlaneConstraintType.Perpendicular, PlaneConstraintType.Parallel, PlaneConstraintType.Coincident, PlaneConstraintType.Project];
    }
    if (
      PlaneCommandRuleEngine.isPointLike(first)
      && (PlaneCommandRuleEngine.isPlaneLike(second) || PlaneCommandRuleEngine.isSurfaceLike(second))
    ) {
      if (PlaneCommandRuleEngine.isSurfaceLike(second)) {
        return [PlaneConstraintType.OnSurface, PlaneConstraintType.Project, PlaneConstraintType.Tangent];
      }
      return [PlaneConstraintType.Parallel, PlaneConstraintType.Coincident, PlaneConstraintType.Project];
    }
    if (
      PlaneCommandRuleEngine.isPointLike(first)
      && PlaneCommandRuleEngine.isPointLike(second)
      && (PlaneCommandRuleEngine.isPlaneLike(third) || PlaneCommandRuleEngine.isSurfaceLike(third))
    ) {
      if (PlaneCommandRuleEngine.isSurfaceLike(third)) {
        return [PlaneConstraintType.OnSurface, PlaneConstraintType.Project, PlaneConstraintType.Tangent];
      }
      return [PlaneConstraintType.Parallel, PlaneConstraintType.Coincident, PlaneConstraintType.Project];
    }
    if (PlaneCommandRuleEngine.isPointLike(first)) {
      return [PlaneConstraintType.Coincident, PlaneConstraintType.Project];
    }
    if (PlaneCommandRuleEngine.isLinear(first) && PlaneCommandRuleEngine.isPointLike(second)) {
      return [PlaneConstraintType.Normal, PlaneConstraintType.Coincident];
    }
    if (
      PlaneCommandRuleEngine.isLinear(first)
      && (PlaneCommandRuleEngine.isPlaneLike(second) || PlaneCommandRuleEngine.isSurfaceLike(second))
    ) {
      return [PlaneConstraintType.Perpendicular, PlaneConstraintType.Coincident, PlaneConstraintType.Angle, PlaneConstraintType.Project];
    }
    if (
      PlaneCommandRuleEngine.isLinear(first)
      && PlaneCommandRuleEngine.isLinear(second)
      && (PlaneCommandRuleEngine.isPlaneLike(third) || PlaneCommandRuleEngine.isSurfaceLike(third))
    ) {
      return [PlaneConstraintType.Perpendicular, PlaneConstraintType.Coincident, PlaneConstraintType.Project];
    }
    if (PlaneCommandRuleEngine.isLinear(first) && !second) {
      return [PlaneConstraintType.Coincident, PlaneConstraintType.Perpendicular, PlaneConstraintType.Project];
    }
    if (PlaneCommandRuleEngine.isLinear(first) && PlaneCommandRuleEngine.isLinear(second)) {
      return [PlaneConstraintType.Coincident, PlaneConstraintType.Perpendicular, PlaneConstraintType.Project];
    }
    if (PlaneCommandRuleEngine.isSurfaceLike(first) && PlaneCommandRuleEngine.isPointLike(second)) {
      return [PlaneConstraintType.OnSurface, PlaneConstraintType.Project, PlaneConstraintType.Tangent];
    }
    return [PlaneConstraintType.Coincident];
  }

  /**
   * 自动推断约束。
   * @param kinds 当前已选参考类型。
   */
  public static inferConstraint(kinds: ReferenceEntitySemanticType[]): PlaneConstraintType {

      /**
     * 单线参考默认应先进入“重合”语义。
     * 这样 PropertyManager 第一步会先和 SolidWorks 一致显示为重合，
     * 等第二参考补入后再由具体组合规则切换到对应求解模式。
     */
    if (PlaneCommandRuleEngine.isLinear(kinds[0]) && !kinds[1]) {
      return PlaneConstraintType.Coincident;
    }


      /**
     * 先选择曲面进入“相切待补参考”后，第二参考若补入点，
     * 默认仍应沿相切语义继续求解，而不是自动切到 OnSurface。
     * 这样 PropertyManager 会保持“曲面=相切、点=重合”的分参考语义。
     */
    if (
      (PlaneCommandRuleEngine.isSurfaceLike(kinds[0]) && PlaneCommandRuleEngine.isPointLike(kinds[1]))
      || (PlaneCommandRuleEngine.isPointLike(kinds[0]) && PlaneCommandRuleEngine.isSurfaceLike(kinds[1]))
    ) {
      return PlaneConstraintType.Tangent;
    }


      if (
      (PlaneCommandRuleEngine.isPlaneLike(kinds[0]) || PlaneCommandRuleEngine.isSurfaceLike(kinds[0]))
      && PlaneCommandRuleEngine.isLinear(kinds[1])
    ) {
      return PlaneConstraintType.Perpendicular;
    }
    if (
      PlaneCommandRuleEngine.isLinear(kinds[0])
      && (PlaneCommandRuleEngine.isPlaneLike(kinds[1]) || PlaneCommandRuleEngine.isSurfaceLike(kinds[1]))
    ) {
      return PlaneConstraintType.Perpendicular;
    }
    if (
      PlaneCommandRuleEngine.isLinear(kinds[0])
      && PlaneCommandRuleEngine.isLinear(kinds[1])
      && (PlaneCommandRuleEngine.isPlaneLike(kinds[2]) || PlaneCommandRuleEngine.isSurfaceLike(kinds[2]))
    ) {
      return PlaneConstraintType.Perpendicular;
    }
    if (PlaneCommandRuleEngine.isLinear(kinds[0]) && PlaneCommandRuleEngine.isLinear(kinds[1])) {
      return PlaneConstraintType.Coincident;
    }

    const available = PlaneCommandRuleEngine.resolveAvailableConstraints(kinds);
    if (available.includes(PlaneConstraintType.MidPlane)) {
      return PlaneConstraintType.MidPlane;
    }
    if (available.includes(PlaneConstraintType.Angle)) {
      return PlaneConstraintType.Angle;
    }
    if (available.includes(PlaneConstraintType.Normal)) {
      return PlaneConstraintType.Normal;
    }
    if (available.includes(PlaneConstraintType.OnSurface)) {
      return PlaneConstraintType.OnSurface;
    }
    if (available.includes(PlaneConstraintType.Tangent)) {
      return PlaneConstraintType.Tangent;
    }
    if (available.includes(PlaneConstraintType.Perpendicular)) {
      return PlaneConstraintType.Perpendicular;
    }
    if (available.includes(PlaneConstraintType.Parallel)) {
      return PlaneConstraintType.Parallel;
    }
    if (available.includes(PlaneConstraintType.Distance)) {
      return PlaneConstraintType.Distance;
    }
    return available[0] ?? PlaneConstraintType.Auto;
  }

  /**
   * 根据当前选择推断默认约束。
   * @param inputs 当前参考输入集合。
   */
  public static inferConstraintBySelections(inputs: PlaneModeInferenceInput[]): PlaneModeTransitionResult {
    const planeLikeCount = inputs.filter((item) => (
      PlaneCommandRuleEngine.isPlaneLike(item.kind)
      || PlaneCommandRuleEngine.isSurfaceLike(item.kind)
    )).length;
    const kinds = inputs
      .map((item) => item.kind as ReferenceEntitySemanticType)
      .filter((item) => typeof item === 'string');
    const inferredConstraint = PlaneCommandRuleEngine.inferConstraint(kinds);
    if (inferredConstraint !== PlaneConstraintType.Auto) {
      return { constraint: inferredConstraint, inferred: true };
    }
    if (planeLikeCount >= 2) {
      return { constraint: PlaneConstraintType.MidPlane, inferred: true };
    }
    if (planeLikeCount === 1) {
      return { constraint: PlaneConstraintType.Distance, inferred: true };
    }
    return { constraint: PlaneConstraintType.Auto, inferred: true };
  }

  /**
   * 删除参考后的约束保持策略。
   * @param params 删除后的上下文。
   */
  public static resolveConstraintAfterRemoval(params: {
    current: PlaneConstraintType;
    modeSource: ReferenceCommandModeSource;
    inputs: PlaneModeInferenceInput[];
  }): PlaneModeTransitionResult {
    const { current, modeSource, inputs } = params;
    if (modeSource === ReferenceCommandModeSource.ManualLocked) {
      return { constraint: current, inferred: false };
    }
    return PlaneCommandRuleEngine.inferConstraintBySelections(inputs);
  }
}
